/**
 * ObservabilityIssuesPanel — the observability agent's output, rendered as a tab
 * inside the Agents page (AgentControlPage).
 *
 * Observability issues are raw signals (backend 5xx + frontend JS crashes),
 * deduped and LLM-triaged. They are NOT auto-promoted into the RCA/ticket queue
 * (see backend jobs/observability_tasks.py) — an ops human reviews each issue and
 * clicks "Create ticket" to promote it (POST …/create-ticket), or "Dismiss" to
 * mark it noise (POST …/dismiss). This mirrors how Jira / customer-support tickets
 * feed the same rca_jobs queue, but adds the create-gate that raw signals need.
 *
 * Ops/super only (the backend endpoints enforce require_ops_or_higher).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Ticket, XCircle, Loader2, ChevronRight, ChevronDown, User, Building2 } from 'lucide-react';
import { authFetch, authFetchJson } from '@/lib/authFetch';

interface IssueRow {
  id: number;
  source: string;
  status_code: number | null;
  method: string | null;
  path: string | null;
  error_type: string | null;
  error_message: string | null;
  occurrence_count: number;
  first_seen_at: string;
  last_seen_at: string;
  severity: string | null;
  status: string;
  rca_job_id: number | null;
  rca_status: string | null;
  jira_key: string | null;
}

// The org's Jira site — promoted observability issues become TRUS Bugs the
// cloud routine then works. Linkable straight from the feed.
const JIRA_BROWSE_BASE = 'https://psyssurance.atlassian.net/browse';
interface IssueSummary {
  total?: number; open?: number; handed_off?: number;
  critical?: number; high?: number; medium?: number; low?: number; untriaged?: number;
}
interface Actor { kind: string; id: string; name: string | null; email: string | null; role: string | null; }
interface IssueDetail extends IssueRow {
  traceback: string | null;
  session_id: string | null;
  cbo_id: number | null;
  triage_summary: string | null;
  first_seen_at: string;
  actor: Actor | null;
  organization: { id: number; name: string | null } | null;
}
interface DetailState { loading: boolean; data: IssueDetail | null; error: boolean; }

const SEV_CLASS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-sky-100 text-sky-700',
};

function SeverityBadge({ severity }: { severity: string | null }) {
  const cls = (severity && SEV_CLASS[severity]) || 'bg-gray-100 text-gray-600';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{severity || 'untriaged'}</span>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-xs text-gray-800 break-words">{children || <span className="text-gray-400">—</span>}</div>
    </div>
  );
}

// The expanded per-issue context: who hit it, what action/route, the error +
// traceback, and the triage hypothesis — everything ops needs before Create ticket.
function IssueDetailView({ state, fmt }: { state?: DetailState; fmt: (s: string) => string }) {
  if (!state || state.loading) {
    return <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin" /> Loading context…</div>;
  }
  if (state.error || !state.data) {
    return <div className="text-xs text-red-600">Couldn't load issue detail.</div>;
  }
  const d = state.data;
  const actorLabel = d.actor
    ? (d.actor.name || d.actor.email || `${d.actor.kind} user ${d.actor.id}`)
    : (d.actor === null && d.status ? 'Anonymous / not signed in' : '—');
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
        <Field label="User">
          <span className="inline-flex items-center gap-1"><User className="w-3 h-3 text-gray-400" />{actorLabel}</span>
          {d.actor?.email && d.actor?.name && <div className="text-gray-500">{d.actor.email}</div>}
          {d.actor?.role && <div className="text-gray-500">{d.actor.role} · {d.actor.kind}</div>}
        </Field>
        <Field label="Organization">
          <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3 text-gray-400" />{d.organization?.name || (d.cbo_id ? `CBO ${d.cbo_id}` : null)}</span>
        </Field>
        <Field label="Action / route">
          <span className="font-mono">{d.method ? `${d.method} ` : ''}{d.path || '—'}{d.status_code ? ` · ${d.status_code}` : ''}</span>
        </Field>
        <Field label="Session">{d.session_id ? <span className="font-mono">{d.session_id}</span> : null}</Field>
        <Field label="First seen">{fmt(d.first_seen_at)}</Field>
        <Field label="Last seen">{fmt(d.last_seen_at)}</Field>
        <Field label="Occurrences">{d.occurrence_count}</Field>
        <Field label="Source">{d.source}</Field>
        <Field label="Ticket">
          {d.jira_key
            ? <a href={`${JIRA_BROWSE_BASE}/${d.jira_key}`} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">{d.jira_key}</a>
            : null}
        </Field>
      </div>

      {d.triage_summary && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Triage hypothesis</div>
          <div className="text-xs text-gray-700">{d.triage_summary}</div>
        </div>
      )}

      <div>
        <div className="text-[11px] uppercase tracking-wide text-gray-400">Error</div>
        <div className="text-xs text-gray-900 font-medium">{d.error_type || '—'}</div>
        {d.error_message && <div className="text-xs text-gray-600 break-words">{d.error_message}</div>}
      </div>

      {d.traceback && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Traceback / stack</div>
          <pre className="mt-1 text-[11px] leading-snug text-gray-700 bg-white border border-gray-200 rounded-md p-2 overflow-auto max-h-64 whitespace-pre-wrap break-words">{d.traceback}</pre>
        </div>
      )}
    </div>
  );
}

interface Props {
  /** Deep-link target from the observability alert notification (?issue=N). */
  focusIssueId?: number | null;
}

export const ObservabilityIssuesPanel: React.FC<Props> = ({ focusIssueId }) => {
  const [issues, setIssues] = useState<IssueRow[]>([]);
  const [summary, setSummary] = useState<IssueSummary>({});
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [details, setDetails] = useState<Record<number, DetailState>>({});

  // Lazy-load the full context (traceback, who, what action, org) for one issue.
  // Cached per id; re-expanding won't refetch.
  const loadDetail = useCallback((id: number) => {
    setDetails((prev) => {
      if (prev[id]?.data || prev[id]?.loading) return prev;
      authFetchJson<IssueDetail>(`/api/admin/observability/issues/${id}`)
        .then((d) => setDetails((p) => ({ ...p, [id]: { loading: false, data: d, error: false } })))
        .catch(() => setDetails((p) => ({ ...p, [id]: { loading: false, data: null, error: true } })));
      return { ...prev, [id]: { loading: true, data: null, error: false } };
    });
  }, []);

  const toggleExpand = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = !prev[id];
      if (next) loadDetail(id);
      return { ...prev, [id]: next };
    });
  }, [loadDetail]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = `days=${days}&limit=500`;
      const [d, s] = await Promise.all([
        authFetchJson<{ issues: IssueRow[] }>(`/api/admin/observability/issues?${q}`),
        authFetchJson<IssueSummary>(`/api/admin/observability/summary?${q}`),
      ]);
      setIssues(d.issues || []);
      setSummary(s || {});
    } catch (e: any) {
      setError(e?.message || 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // Deep link from the alert notification (?issue=N): auto-expand that row so the
  // reviewer lands directly on its full context.
  useEffect(() => {
    if (focusIssueId != null && issues.some((it) => it.id === focusIssueId) && !expanded[focusIssueId]) {
      setExpanded((p) => ({ ...p, [focusIssueId]: true }));
      loadDetail(focusIssueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusIssueId, issues]);

  const act = useCallback(async (id: number, action: 'create-ticket' | 'dismiss') => {
    setBusyId(id);
    try {
      const res = await authFetch(`/api/admin/observability/issues/${id}/${action}`, { method: 'POST' });
      if (!res.ok) throw new Error(`${res.status}`);
      await load();
    } catch {
      setError(`Could not ${action === 'create-ticket' ? 'create ticket' : 'dismiss'} for issue ${id}`);
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const fmt = (s: string) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {([
          ['critical', summary.critical, 'bg-red-100 text-red-700'],
          ['high', summary.high, 'bg-orange-100 text-orange-700'],
          ['medium', summary.medium, 'bg-amber-100 text-amber-700'],
          ['low', summary.low, 'bg-sky-100 text-sky-700'],
          ['untriaged', summary.untriaged, 'bg-gray-100 text-gray-600'],
        ] as const).map(([label, count, cls]) => (
          <span key={label} className={`text-xs px-2.5 py-1 rounded-full ${cls}`}>
            {label}: <strong>{Number(count || 0)}</strong>
          </span>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-xs rounded-md border border-gray-300 bg-white px-2 py-1"
          >
            {[7, 30, 90].map((d) => <option key={d} value={d}>Last {d}d</option>)}
          </select>
          <button onClick={load} className="text-xs inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 hover:bg-gray-50">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
        Errors users actually hit — deduped and LLM-triaged. Nothing is auto-ticketed: review an issue and
        <strong> Create ticket</strong> to hand it to the RCA pipeline (behind the usual fix-approval gate), or
        <strong> Dismiss</strong> if it's noise.
      </div>

      {error && <div className="text-xs text-red-600">{error}</div>}

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs">
            <tr>
              {['Severity', 'Source', 'Issue', 'Count', 'Last seen', 'State', ''].map((h) => (
                <th key={h} className="text-left font-medium px-3 py-2 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {issues.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No issues in this window. 🎉</td></tr>
            )}
            {issues.map((it) => {
              const promoted = it.status === 'handed_off' || !!it.rca_job_id;
              const dismissed = it.status === 'ignored';
              const focused = focusIssueId != null && it.id === focusIssueId;
              const isOpen = !!expanded[it.id];
              return (
              <React.Fragment key={it.id}>
                <tr className={focused ? 'bg-amber-50' : undefined}>
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-start gap-1.5">
                      <button
                        type="button"
                        onClick={() => toggleExpand(it.id)}
                        className="mt-0.5 text-gray-400 hover:text-gray-700"
                        aria-expanded={isOpen}
                        title={isOpen ? 'Hide detail' : 'Show detail'}
                      >
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      <SeverityBadge severity={it.severity} />
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-gray-600 whitespace-nowrap">{it.source}</td>
                  <td className="px-3 py-2 align-top max-w-[24rem]">
                    <button type="button" onClick={() => toggleExpand(it.id)} className="text-left w-full">
                      <div className="font-mono text-xs text-gray-900 truncate" title={it.path || ''}>
                        {it.method ? `${it.method} ` : ''}{it.path || it.error_type || '—'}
                        {it.status_code ? <span className="text-red-600"> · {it.status_code}</span> : null}
                      </div>
                      {it.error_message && (
                        <div className="text-xs text-gray-500 truncate" title={it.error_message}>{it.error_message}</div>
                      )}
                    </button>
                  </td>
                  <td className="px-3 py-2 align-top font-semibold text-gray-900">{it.occurrence_count}</td>
                  <td className="px-3 py-2 align-top text-xs text-gray-500 whitespace-nowrap">{fmt(it.last_seen_at)}</td>
                  <td className="px-3 py-2 align-top text-xs whitespace-nowrap">
                    {it.jira_key ? (
                      <a
                        href={`${JIRA_BROWSE_BASE}/${it.jira_key}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                      >
                        <Ticket className="w-3 h-3" /> {it.jira_key}
                      </a>
                    ) : promoted ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <Ticket className="w-3 h-3" /> Ticket #{it.rca_job_id}{it.rca_status ? ` · ${it.rca_status}` : ''}
                      </span>
                    ) : dismissed ? (
                      <span className="text-gray-400">Dismissed</span>
                    ) : (
                      <span className="text-gray-500">{it.status}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top whitespace-nowrap text-right">
                    {!promoted && !dismissed && (
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          onClick={() => act(it.id, 'create-ticket')}
                          disabled={busyId === it.id}
                          className="text-xs inline-flex items-center gap-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 disabled:opacity-50"
                        >
                          {busyId === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ticket className="w-3 h-3" />}
                          Create ticket
                        </button>
                        <button
                          onClick={() => act(it.id, 'dismiss')}
                          disabled={busyId === it.id}
                          className="text-xs inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-50"
                          title="Mark as noise"
                        >
                          <XCircle className="w-3 h-3" /> Dismiss
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="bg-gray-50/70">
                    <td colSpan={7} className="px-4 py-3">
                      <IssueDetailView state={details[it.id]} fmt={fmt} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ObservabilityIssuesPanel;
